import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError, TimeoutError } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { BaseService, ConfigService, LoggingService, UtilsMisc, UtilsString, retryWithDelay, ErrorTimeout, ErrorHttp, ErrorUnknown } from '@pit/core';
import { ICommunicationDataService } from '../interfaces/communication.data.service.interface';

@Injectable({
  providedIn: 'root'
})
export class CommunicationDataService extends BaseService implements ICommunicationDataService {

  constructor(private http: HttpClient, configService: ConfigService, loggingService: LoggingService) {
    super(configService, loggingService);
  }

  public sendMessageToDevices$(message: string, expiresAt: Date, devices: string[]): Observable<boolean> {
    const body: any = {
      authenticationUserName: this.configurationService.configAuth.authUserName,
      authenticationPassword: this.configurationService.configAuth.authPassword,
      message: message,
      messageType: 'devices',
      receivers: devices
    };
    return this.httpPost(this.getUrl('messages/send'), body)
      .pipe(map((json: boolean) => {
        return json;
      }));
  }

  public async sendMessageToDevicesAsync(message: string, expiresAt: Date, devices: string[]): Promise<boolean> {
    return (await this.sendMessageToDevices$(message, expiresAt, devices).toPromise());
  }

  public sendMessageToDepots$(mandator: string, message: string, expiresAt: Date, depots: string[]): Observable<boolean> {
    const body: any = {
      authenticationUserName: this.configurationService.configAuth.authUserName,
      authenticationPassword: this.configurationService.configAuth.authPassword,
      mandator: mandator,
      message: message,
      messageType: 'depots',
      receivers: depots
    };
    return this.httpPost(this.getUrl('messages/send'), body)
      .pipe(map((json: boolean) => {
        return json;
      }));
  }

  public async sendMessageToDepotsAsync(mandator: string, message: string, expiresAt: Date, depots: string[]): Promise<boolean> {
    return (await this.sendMessageToDepots$(mandator, message, expiresAt, depots).toPromise());
  }

  public sendShipmentsLoadAssignedForDevice$(deviceId: string, expiresAt: Date): Observable<boolean> {
    return this.httpPost(this.getUrl(UtilsString.stringFormat('shipments/loadassigned/device/{0}', deviceId)), null)
      .pipe(map((json: boolean) => {
        return json;
      }));
  }

  public async sendShipmentsLoadAssignedForDeviceAsync(deviceId: string, expiresAt: Date): Promise<boolean> {
    return (await this.sendShipmentsLoadAssignedForDevice$(deviceId, expiresAt).toPromise());
  }

  public sendDeviceLogout$(deviceId: string, expiresAt: Date): Observable<boolean> {
    return this.httpPost(this.getUrl(UtilsString.stringFormat('devices/{0}/logout', deviceId)), null)
      .pipe(map((json: boolean) => {
        return json;
      }));
  }

  public async sendDeviceLogoutAsync(deviceId: string, expiresAt: Date): Promise<boolean> {
    return (await this.sendDeviceLogout$(deviceId, expiresAt).toPromise());
  }

  public sendDeviceReset$(deviceId: string, expiresAt: Date): Observable<boolean> {
    return this.httpPost(this.getUrl(UtilsString.stringFormat('devices/{0}/reset', deviceId)), null)
      .pipe(map((json: boolean) => {
        return json;
      }));
  }

  public async sendDeviceResetAsync(deviceId: string, expiresAt: Date): Promise<boolean> {
    return (await this.sendDeviceReset$(deviceId, expiresAt).toPromise());
  }

  public sendDeviceFinishWorkDay$(deviceId: string, expiresAt: Date): Observable<boolean> {
    return this.httpPost(this.getUrl(UtilsString.stringFormat('devices/{0}/finishworkday', deviceId)), null)
      .pipe(map((json: boolean) => {
        return json;
      }));
  }

  public async sendDeviceFinishWorkDayAsync(deviceId: string, expiresAt: Date): Promise<boolean> {
    return (await this.sendDeviceFinishWorkDay$(deviceId, expiresAt).toPromise());
  }

  public requestDeviceBatteryInfo$(deviceId: string, expiresAt: Date): Observable<boolean> {
    return this.httpPost(this.getUrl(UtilsString.stringFormat('devices/{0}/batteryinfo', deviceId)), null)
      .pipe(map((json: boolean) => {
        return json;
      }));
  }

  public async requestDeviceBatteryInfoAsync(deviceId: string, expiresAt: Date): Promise<boolean> {
    return (await this.requestDeviceBatteryInfo$(deviceId, expiresAt).toPromise());
  }

  public requestDeviceSync$(deviceId: string, expiresAt: Date): Observable<boolean> {
    return this.httpPost(this.getUrl(UtilsString.stringFormat('devices/{0}/forcesync', deviceId)), null)
      .pipe(map((json: boolean) => {
        return json;
      }));
  }

  public async requestDeviceSyncAsync(deviceId: string, expiresAt: Date): Promise<boolean> {
    return (await this.requestDeviceSync$(deviceId, expiresAt).toPromise());
  }

  public requestDeviceAppExit$(deviceId: string, expiresAt: Date): Observable<boolean> {
    return this.httpPost(this.getUrl(UtilsString.stringFormat('devices/{0}/forceappexit', deviceId)), null)
      .pipe(map((json: boolean) => {
        return json;
      }));
  }

  public async requestDeviceAppExitAsync(deviceId: string, expiresAt: Date): Promise<boolean> {
    return (await this.requestDeviceAppExit$(deviceId, expiresAt).toPromise());
  }

  public requestDeviceAppLock$(deviceId: string, expiresAt: Date): Observable<boolean> {
    return this.httpPost(this.getUrl(UtilsString.stringFormat('devices/{0}/forceapplock', deviceId)), null)
      .pipe(map((json: boolean) => {
        return json;
      }));
  }

  public async requestDeviceAppLockAsync(deviceId: string, expiresAt: Date): Promise<boolean> {
    return (await this.requestDeviceAppLock$(deviceId, expiresAt).toPromise());
  }

  public requestDeviceAppUnlock$(deviceId: string, expiresAt: Date): Observable<boolean> {
    return this.httpPost(this.getUrl(UtilsString.stringFormat('devices/{0}/forceappunlock', deviceId)), null)
      .pipe(map((json: boolean) => {
        return json;
      }));

  }

  public async requestDeviceAppUnlockAsync(deviceId: string, expiresAt: Date): Promise<boolean> {
    return (await this.requestDeviceAppUnlock$(deviceId, expiresAt).toPromise());
  }

  public requestDeviceAppUnlockHome$(deviceId: string, expiresAt: Date): Observable<boolean> {
    return this.httpPost(this.getUrl(UtilsString.stringFormat('devices/{0}/forceappunlockhome', deviceId)), null)
      .pipe(map((json: boolean) => {
        return json;
      }));
  }

  public async requestDeviceAppUnlockHomeAsync(deviceId: string, expiresAt: Date): Promise<boolean> {
    return (await this.requestDeviceAppUnlockHome$(deviceId, expiresAt).toPromise());
  }

  public requestDeviceHttpModeOptions$(deviceId: string, expiresAt: Date, httpMode: string, httpModeSingleton: boolean): Observable<boolean> {
    const body: any = {
      httpMode: httpMode,
      httpModeSingleton: httpModeSingleton
    };
    return this.httpPost(this.getUrl(UtilsString.stringFormat('devices/{0}/forcehttpmodeoptions', deviceId)), body)
      .pipe(map((json: boolean) => {
        return json;
      }));
  }

  public async requestDeviceHttpModeOptionsAsync(deviceId: string, expiresAt: Date, httpMode: string, httpModeSingleton: boolean): Promise<boolean> {
    return (await this.requestDeviceHttpModeOptions$(deviceId, expiresAt, httpMode, httpModeSingleton).toPromise());
  }

  public requestDeviceLoadSettings$(deviceId: string, expiresAt: Date): Observable<boolean> {
    return this.httpPost(this.getUrl(UtilsString.stringFormat('devices/{0}/loadsettings', deviceId)), null)
      .pipe(map((json: boolean) => {
        return json;
      }));
  }

  public async requestDeviceLoadSettingsAsync(deviceId: string, expiresAt: Date): Promise<boolean> {
    return (await this.requestDeviceLoadSettings$(deviceId, expiresAt).toPromise());
  }

  public requestDeviceBackgroundFetch$(deviceId: string, expiresAt: Date, fetchOn: boolean): Observable<boolean> {
    return this.httpPost(this.getUrl(UtilsString.stringFormat('devices/{0}/backgroundfetch/{1}', deviceId, fetchOn)), null)
      .pipe(map((json: boolean) => {
        return json;
      }));
  }

  public async requestDeviceBackgroundFetchAsync(deviceId: string, expiresAt: Date, fetchOn: boolean): Promise<boolean> {
    return (await this.requestDeviceBackgroundFetch$(deviceId, expiresAt, fetchOn).toPromise());
  }

  public requestDevicePosition$(deviceId: string, expiresAt: Date): Observable<boolean> {
    return this.httpPost(this.getUrl(UtilsString.stringFormat('devices/{0}/position', deviceId)), null)
      .pipe(map((json: boolean) => {
        return json;
      }));
  }

  public async requestDevicePositionAsync(deviceId: string, expiresAt: Date): Promise<boolean> {
    return (await this.requestDevicePosition$(deviceId, expiresAt).toPromise());
  }

  protected handleHttpError(error: Error): Observable<never> {
    let result: Error = error;
    if (error instanceof TimeoutError) {
      result = new ErrorTimeout();
    } else if (error instanceof HttpErrorResponse) {
      const httpError: HttpErrorResponse = (error as HttpErrorResponse);
      result = new ErrorHttp(httpError.status, httpError.statusText, ((UtilsMisc.isNullOrUndefined(httpError.error) === false) ? httpError.error.errorMessage : httpError.statusText));
      switch (httpError.status) {
        case 0: {
          result.message = 'Es konnte keine Verbindung zum Server aufgebaut werden.';
          break;
        }
        case 403: {
          result.message = 'Keine Berechtigung für Serveraufrufe.';
          break;
        }
      }
    } else {
      result = new ErrorUnknown(error.name, error.message);
    }
    this.loggingService.error(result);
    return throwError(result);
  }

  protected httpPost(url: string, body: any, timeoutInMilliSeconds: number = null, maxRetries: number = null, retryDelay: number = null): Observable<any> {
    if (UtilsMisc.isNullOrUndefined(timeoutInMilliSeconds) === true) {
      timeoutInMilliSeconds = this.configurationService.configData.timeout;
    }
    if (UtilsMisc.isNullOrUndefined(maxRetries) === true) {
      maxRetries = this.configurationService.configData.retries;
    }
    if (UtilsMisc.isNullOrUndefined(retryDelay) === true) {
      retryDelay = this.configurationService.configData.retryDelay;
    }
    return this.http.post(url, body).pipe(
      timeout(timeoutInMilliSeconds),
      catchError(this.handleHttpError.bind(this)),
      retryWithDelay(retryDelay, maxRetries)
    );
  }

  protected getUrl(url: string): string {
    return `/api/comm/${url}/`;
  }

}
